import TinyQueue from "tinyqueue";
import { makeImmutable } from "../../core";
import { Sentry } from "../../Sentry";
import { Packing } from "./Packing";
import { PackagingContainerInstance } from "./PackagingContainerInstance";
import { UnpackableItemError } from "./UnpackableItemError";
// NOTICE Largest first: a big item dropped in late has nowhere to go but a
// container of its own, while small ones still fit the gaps it leaves behind.
const largestItemFirst = (a, b) => b.productVolume.compare(a.productVolume);
const cheapestFirst = (a, b) => a.price.cents - b.price.cents || a.capacity.compare(b.capacity);
// Capacity per unit of price, compared without dividing so a container of zero
// capacity does not turn into a division by zero.
const bestValueFirst = (a, b) => a.price.cents * b.capacity.value.valueOf() -
    b.price.cents * a.capacity.value.valueOf() ||
    b.capacity.compare(a.capacity);
/**
 * Packs products and modifiers into containers, largest item first, each into
 * the cheapest container that can hold it.
 */
export class PackingAlgorithm {
    constructor(params) {
        this.packagingContainerTypes = params.packagingContainerTypes;
        makeImmutable(this);
    }
    allowedContainerTypes(item) {
        return this.packagingContainerTypes.objects.filtered((containerType) => containerType.allows(item.subject));
    }
    /**
     * The cheapest container that can hold what is left of the item.
     *
     * One that takes the whole remainder wins - there is nothing to gain from
     * paying for a second - and among equally priced ones the smaller, so a
     * single sauce does not claim the big box. When nothing holds the remainder
     * in one go the item is going to be split anyway, and the most capacity per
     * unit of price wins instead.
     */
    makeContainer(item) {
        const availableContainerTypes = this.allowedContainerTypes(item).filtered((containerType) => containerType.canContain(item.subject));
        if (availableContainerTypes.isEmpty) {
            return null;
        }
        const holdingWholeItem = availableContainerTypes.filtered((containerType) => containerType.capacity.compare(item.totalVolume) >= 0);
        const packagingContainerType = holdingWholeItem.isEmpty
            ? availableContainerTypes.sorted(bestValueFirst).get(0)
            : holdingWholeItem.sorted(cheapestFirst).get(0);
        return new PackagingContainerInstance({ packagingContainerType });
    }
    pack(items) {
        // NOTICE TinyQueue heapifies in place, so it gets a copy rather than the
        // array backing the caller's RArray.
        const itemsToPack = new TinyQueue([...items.objects.raw], largestItemFirst);
        const packing = Packing.empty();
        while (itemsToPack.length > 0) {
            const itemToPack = itemsToPack.pop();
            const result = packing.pack(itemToPack);
            if (result.type === "packed") {
                continue;
            }
            if (this.allowedContainerTypes(result.remainder).isEmpty) {
                // NOTICE No container accepts this subject, so the backend reports no
                // packaging demand for it either - it is simply not packaged.
                continue;
            }
            const nextContainer = this.makeContainer(result.remainder);
            if (nextContainer) {
                packing.addContainer(nextContainer);
                itemsToPack.push(result.remainder);
            }
            else {
                // The backend does demand packaging for this subject and will reject
                // the order because we cannot supply a container for it.
                Sentry.captureException(new UnpackableItemError(result.remainder), {
                    level: "error",
                });
            }
        }
        return packing;
    }
}
